import IFocusableActions from '../Actions/Interfaces/IFocusableActions';
import FocusableEvent from '../Events/FocusableEvent';
import THandler from '../Shared/Types/THandler';

import Alignment, {TAlignment} from './Constants/Alignment';
import FocusableNodeType, {TFocusableNodeType} from './Constants/FocusableNodeType';

/**
 * The fundamental building block representing a focusable UI element in the tree of
 * components.
 *
 * Each `FocusableNode` instance holds various data about the type, behaviour and position
 * of the node in its containing tree, as well as methods with which to invoke input
 * events on a node's associated React component instance.
 */

class FocusableNode {
    public get isRoot(): boolean {
        return this.parentId === '';
    }

    public get isLeaf(): boolean {
        return this.type === FocusableNodeType.LEAF;
    }

    public get isContainer(): boolean {
        return this.type !== FocusableNodeType.LEAF;
    }

    public get isGrid(): boolean {
        return this.type === FocusableNodeType.GRID;
    }

    public get isVirtualHorizontalList(): boolean {
        return this.type === FocusableNodeType.VIRTUAL_HORIZONTAL_LIST;
    }

    public get isVirtualVerticalList(): boolean {
        return this.type === FocusableNodeType.VIRTUAL_VERTICAL_LIST;
    }

    public get isVirtualContainer(): boolean {
        return this.isVirtualHorizontalList || this.isVirtualVerticalList;
    }

    public anchors: number[] = [50];
    public index: number = -1;
    public basis: number = -1;
    public id: string = '';
    public parentId: string = '';
    public lastDirectionOfTravel: number = 0;
    public type: TFocusableNodeType = FocusableNodeType.LEAF;
    public parent: FocusableNode | null = null;
    public previousSibling: FocusableNode | null = null;
    public children: FocusableNode[] = [];
    public focus!: (event: FocusableEvent) => void;
    public blur!: (event: FocusableEvent) => void;
    public select!: (event: FocusableEvent) => void;
    public up!: (event: FocusableEvent) => void;
    public down!: (event: FocusableEvent) => void;
    public left!: (event: FocusableEvent) => void;
    public right!: (event: FocusableEvent) => void;
    public focuswillchange!: (event: FocusableEvent) => void;
    public focuschange!: (event: FocusableEvent) => void;

    // Following properties are created as getters during
    // mapping, mapped directly to component props to ensure
    // live, mutable references

    public readonly actions!: IFocusableActions;
    public readonly basisOverride!: number;
    public readonly anchorsOverride!: number[];
    public readonly delegateChangeFocus!: boolean;
    public readonly isFocusDisabled!: boolean;
    public readonly hasFocusableElement!: boolean;
    public readonly justify!: boolean;
    public readonly finalGridRowAlign!: TAlignment;
    public readonly indexOverride!: number;
    public readonly columns!: number;
    public readonly loop!: boolean;
    public readonly throttleDurationMs!: number;
    public readonly virtualContainerItemsLength!: number;
    public readonly virtualContainerStartIndex!: number;
    public readonly virtualFocusedIndex!: number;

    public static defaultOptions: Pick<
        FocusableNode,
        | 'anchorsOverride'
        | 'basisOverride'
        | 'columns'
        | 'delegateChangeFocus'
        | 'justify'
        | 'finalGridRowAlign'
        | 'loop'
        | 'throttleDurationMs'
        | 'indexOverride'
        | 'isFocusDisabled'
        | 'virtualContainerItemsLength'
        | 'virtualContainerStartIndex'
        | 'virtualFocusedIndex'
    > = {
        anchorsOverride: [],
        basisOverride: NaN,
        columns: 1,
        delegateChangeFocus: false,
        isFocusDisabled: false,
        indexOverride: NaN,
        justify: false,
        finalGridRowAlign: Alignment.LEFT,
        loop: false,
        throttleDurationMs: 0,
        virtualContainerItemsLength: 0,
        virtualContainerStartIndex: 0,
        virtualFocusedIndex: -1,
    };

    constructor() {
        this.dispatchEvent = this.dispatchEvent.bind(this);
    }

    /**
     * Dispatches a provided event with the node as its `target` to
     * all registered handlers, and propagates it up the tree to all
     * parent nodes.
     *
     * @param event The event to be dispatched.
     */

    public dispatchEvent(event: FocusableEvent): void {
        // eslint-disable-next-line @typescript-eslint/no-this-alias
        let node: FocusableNode | null = this;

        event.target = node;

        do {
            const method: THandler = node[event.type];

            method.call(null, event);

            node = node.parent;
        } while (node !== null && event.canPropagate);
    }
}

export default FocusableNode;
